import { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';

import { useDispatch, useSelector } from '../redux/hooks';
import { AuthenticationGroup, signOut } from '../redux/slices/auth';
import { updateShowIdleTimeoutWarningModal } from '../redux/slices/modals';

const INTERACTION_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'wheel',
  'pointerdown',
  'pointermove',
  'focus',
];

const DEBOUNCE_DELAY_MS = 1500;
const IDLE_TIMEOUT_LOGOUT_MS = 16 * 60 * 1000; // 16 minutes
const IDLE_TIMEOUT_WARNING_MS = 15 * 60 * 1000; // 15 minutes
const IDLE_TIMEOUT_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds
const EXCLUDED_ROUTES = [
  '/demo',
  '/forgot-password',
  '/forgot-password-confirm',
  '/forgot-password-submit',
  '/sign-in',
  '/sign-up',
  '/confirm',
];

// Force sign out user if they have have been inactive for 15 minutes
function useUserIdleTimeout() {
  const dispatch = useDispatch();
  const roles = useSelector((state) => state.auth.roles);
  const debouncedHandlerRef = useRef<ReturnType<typeof debounce> | null>(null);
  const lastInteractionTime = useRef(Date.now());
  const demoMode = useSelector((state) => state.demoMode.isEnabled);
  const isOperator = useMemo(() => roles?.some((role) => [AuthenticationGroup.OPERATORS, AuthenticationGroup.VENDOR_MANAGEMENT, AuthenticationGroup.FINANCIERS].includes(role)), [roles]);

  useEffect(() => {
    if (demoMode || isOperator) {
      return () => { };
    }

    const handler = () => {
      lastInteractionTime.current = Date.now();
    };

    debouncedHandlerRef.current = debounce(handler, DEBOUNCE_DELAY_MS);

    INTERACTION_EVENTS.forEach((evt) =>
      window.addEventListener(evt, debouncedHandlerRef?.current ?? (() => { }), { passive: true })
    );

    return () => {
      INTERACTION_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, debouncedHandlerRef.current ?? (() => { }))
      );
      debouncedHandlerRef.current?.cancel();
      debouncedHandlerRef.current = null;
    };
  }, [dispatch, demoMode, isOperator]);

  useEffect(() => {
    if (demoMode || isOperator) {
      return () => { };
    }

    const interval = setInterval(async () => {
      const now = Date.now();
      const timeSinceLastInteraction = now - (lastInteractionTime.current || now);

      if (timeSinceLastInteraction > IDLE_TIMEOUT_WARNING_MS) {
        dispatch(updateShowIdleTimeoutWarningModal(true));
      }

      if (
        timeSinceLastInteraction > IDLE_TIMEOUT_LOGOUT_MS
        && !EXCLUDED_ROUTES.includes(location.pathname)
      ) {
        clearInterval(interval);
        dispatch(updateShowIdleTimeoutWarningModal(false));
        await dispatch(signOut());
        window.location.href = '/sign-in?showIdleTimeoutNotification';
      }
    }, IDLE_TIMEOUT_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dispatch, demoMode, isOperator]);
}

export default useUserIdleTimeout;